/**
 * Deterministic initials-chip colours for the Avatar fallback.
 *
 * Palette = the vivid deck / folder colours. Mirrors flashka-web
 * `index.css` light-mode `--deck-{name}-rgb` (flashka-app's `deck-colors.ts`
 * carries the softer Tailwind-400 twins). Mode-invariant on purpose: like a
 * deck cover's identity colour, an avatar keeps the same hue in light and
 * dark — matching the existing fixed-purple behaviour, just per-name now.
 *
 * The classes are LITERAL strings, never built at runtime, so Tailwind (web)
 * and NativeWind (native) both compile them from this source file — a
 * runtime-assembled `bg-[${hex}]` would be invisible to the class scanner.
 * Initials text is always `secondary-50` (#ffffff) — see AVATAR_TEXT_COLOR.
 */
export const AVATAR_BG_COLORS = [
  'bg-stable-orange-400', // orange
  'bg-stable-amber-400', // amber
  'bg-stable-lime-400', // lime
  'bg-stable-green-400', // green
  'bg-stable-emerald-400', // emerald
  'bg-stable-teal-400', // teal
  'bg-stable-cyan-400', // cyan
  'bg-stable-sky-400', // sky
  'bg-stable-tertiary-400', // blue
  'bg-stable-fuchsia-400', // fuchsia
  'bg-stable-pink-400', // pink
  'bg-stable-rose-400', // rose
] as const;

/** Initials colour — always `secondary-50` (#ffffff), mode-invariant, per design. */
export const AVATAR_TEXT_COLOR = 'text-secondary-50';

/**
 * Pick a stable background class from {@link AVATAR_BG_COLORS} for a name.
 * Normalised (trim → strip leading `@` → lowercase) so "@Marco", "marco",
 * and " Marco " all land on the same colour. Empty / missing name → the
 * first colour, deterministically.
 */
export function avatarBgColorClass(name: string | undefined): string {
  const key = (name ?? '').trim().replace(/^@/, '').toLowerCase();
  if (!key) return AVATAR_BG_COLORS[0];
  let hash = 0;
  for (const ch of key) {
    hash = (Math.imul(hash, 31) + ch.codePointAt(0)!) >>> 0;
  }
  return AVATAR_BG_COLORS[hash % AVATAR_BG_COLORS.length]!;
}
